import { useLocation } from "react-router-dom";
import * as React from "react";
import "dayjs/locale/en-in";
import dayjs from "dayjs";
import { useApolloClient } from "@apollo/client";
import localizedFormat from "dayjs/plugin/localizedFormat";

import { traceEventDetailsV2 } from "../../apiV2";
import TraceV2 from "../../components/TraceV2";
import { Box, Typography, Stack } from "@mui/material";
import "../../loaderStyles.css";
import { TraceItemV2 } from "../../types";
import { formatEpochToDateTime, parseOutputProductInstances } from "../../utils";
import { useOrgDetails } from "../../Hooks/useOrgDetails";
import EmptyTrace from "../UI/EmptyTrace";

dayjs.extend(localizedFormat);
dayjs.locale("en-in");

function useUrlQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function TraceView() {
    const client = useApolloClient();
    let query = useUrlQuery();
    const trace_item = query.get("trace_item") || undefined;
    const trace_item_id = query.get("trace_item_id") || undefined;
    const event_id = query.get("event_id") || undefined;
    const trace_direction = query.get("trace_direction") || undefined;
    let org_id = query.get("org_id") || undefined;
    if (org_id === undefined) {
        let item_parts = trace_item_id?.split("-") || [];
        if (item_parts.length > 2) {
            if (item_parts[0] === "g") {
                org_id = item_parts[1];
            } else {
                org_id = item_parts[0];
            }
        }
    }

    const { orgInfo } = useOrgDetails(org_id);

    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(false);
    const [traces, setTraces] = React.useState<TraceItemV2[]>([]);


    React.useEffect(() => {
        const getTraces = async () => {
            if ((!trace_item && !trace_item_id) || !org_id) {
                window.location.href = "https://tralexho.com/";
                return;
            }

            try {
                const results = await traceEventDetailsV2({ traceItem: trace_item_id!, orgId: org_id, eventId: event_id, traceDirection: trace_direction, client });

                const { traceDetails: traceEventsList } = results.traceEventDetails;

                const tmpResults: TraceItemV2[] = [];
                traceEventsList.forEach((traceEvent: any) => {
                    const eventTime = traceEvent.eventStartUnixEpoch ? formatEpochToDateTime(traceEvent.eventStartUnixEpoch) : null;
                    const item: TraceItemV2 = {
                        eventID: traceEvent.eventId,
                        userEvent: traceEvent.userEvent,
                        eventDescription: traceEvent.eventDescription,
                        eventStartUnixEpoch: traceEvent.eventStartUnixEpoch,
                        eventTime: eventTime,
                        location: traceEvent.locationName,
                        latitude: traceEvent.latitude,
                        longitude: traceEvent.longitude,
                        eventDefId: Number(traceEvent?.eventDefId),
                        formDetails: traceEvent?.formDetails,
                        outputProductInstances: parseOutputProductInstances(traceEvent?.outputProductInstances)
                    };
                    tmpResults.push(item);
                });
                setTraces(tmpResults);

                setLoading(false);
            } catch (error) {
                setError(true);
                setLoading(false);
            }
        };

        getTraces();
    }, [client, org_id, trace_item, trace_item_id, event_id, trace_direction]);

    const renderTrace = () => {
        if (traces && orgInfo) {
            return <TraceV2 traces={traces} orgInfo={orgInfo} traceItemID={trace_item_id} orgId={org_id} />;
        }
        return null;
    };

    return (
        <Box sx={{ maxWidth: "100vw", minHeight: "calc(var(--app-vh, 1vh) * 100)", display: "flex" }}>
            {loading ? (
                <Stack spacing={2} sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100vw", height: "calc(var(--app-vh, 1vh) * 100)" }}>
                    <Typography variant="h6" sx={{ color: '#000000' }}>Loading Your Product Journey...</Typography>
                    <div className="loader loader-3">
                        <div></div><div></div><div></div>
                    </div>
                </Stack>
            ) : error ? (
                <EmptyTrace />
            ) : (
                renderTrace()
            )}
        </Box>
    );
}
